import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight, ArrowLeft, ShieldAlert, ShieldCheck, Home, Building2, Car,
  Landmark, Gavel, ArrowUpRight, ArrowDownLeft, Sparkles, AlertCircle, Info,
  Shield, Download, FileSignature, Search as SearchIcon,
} from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import ConsolidatedAssetSummary from "@/components/queries/ConsolidatedAssetSummary";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { downloadAuthFile } from "@/lib/downloadFile";
import { centsToUSD, shortDate } from "@/lib/dates";
import { TIERS } from "@/lib/pricing";
import { cn } from "@/lib/utils";
import { RECOVERY_TONE } from "@/lib/queries";

const Section = ({ title, subtitle, children, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
    className="bg-bg-secondary border border-border-default rounded-[12px] p-6 space-y-4"
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-accent-pink font-semibold text-[13px] tracking-[0.08em] uppercase">{title}</div>
        {subtitle && <div className="text-text-secondary text-[13px] mt-1">{subtitle}</div>}
      </div>
    </div>
    {children}
  </motion.section>
);

const ChangeRow = ({ c }) => {
  const isAdded = c.action === "added";
  const isChanged = c.action === "changed";
  const tone = c.severity === "danger" ? "text-danger" : c.severity === "warning" ? "text-warning" : "text-text-secondary";
  const badgeStyle = isAdded ? "bg-success/15 text-success border-success/40"
    : isChanged ? "bg-warning/15 text-warning border-warning/40"
    : "bg-text-muted/15 text-text-muted border-text-muted/40";

  const describe = (v) => {
    if (v == null) return "—";
    if (typeof v === "number") return centsToUSD(v);
    if (typeof v === "object") {
      if (v.street) return `${v.street}, ${v.city || ""} ${v.state || ""}`.trim();
      if (v.name) return v.name;
      if (v.address) return v.address;
      if (v.year || v.make) return [v.year, v.make, v.model].filter(Boolean).join(" ");
      return JSON.stringify(v);
    }
    return String(v);
  };

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border-default last:border-0" data-testid="change-log-row">
      <span className={cn("inline-flex px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-[0.08em] uppercase shrink-0", badgeStyle)}>
        {c.action}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-white text-[13px] font-medium capitalize">{(c.field || "").replace(/_/g, " ")}</div>
        <div className="text-text-secondary text-[12px] mt-0.5 flex flex-wrap items-center gap-1.5">
          {c.before != null && (
            <>
              <ArrowDownLeft size={11} className="text-text-muted" />
              <span className="line-through decoration-text-muted/60">{describe(c.before)}</span>
            </>
          )}
          {c.after != null && (
            <>
              <ArrowUpRight size={11} className={tone} />
              <span className={tone}>{describe(c.after)}</span>
            </>
          )}
          <span className="text-text-muted ml-2">via {c.source_broker}</span>
        </div>
      </div>
    </div>
  );
};

const AlertRow = ({ a }) => {
  const Icon = a.severity === "danger" ? AlertCircle : a.severity === "warning" ? AlertCircle : Info;
  const tone = a.severity === "danger" ? "text-danger" : a.severity === "warning" ? "text-warning" : "text-text-secondary";
  const border = a.severity === "danger" ? "border-l-2 border-danger bg-danger/5"
    : a.severity === "warning" ? "border-l-2 border-warning bg-warning/5"
    : "border-l-2 border-accent-pink/40 bg-accent-pink/5";
  return (
    <div className={cn("rounded-[6px] px-4 py-3 flex items-start gap-3", border)} data-testid="report-alert-row">
      <Icon size={16} className={tone} />
      <div className="flex-1">
        <div className="text-white text-[13px]">{a.message}</div>
        {a.source_broker && <div className="text-text-muted text-[11px] mt-0.5">Source: {a.source_broker}</div>}
      </div>
    </div>
  );
};

const RecommendationCard = ({ r, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.28, delay: index * 0.06 }}
    className="bg-bg-primary border border-border-default rounded-[12px] p-5 hover:border-accent-pink/40 transition-colors"
    data-testid={`recommendation-card-${r.id}`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-accent-pink font-bold text-[11px] tracking-[0.08em] uppercase">
            Priority {r.priority}
          </span>
          {r.priority === 1 && (
            <span className="text-[10px] bg-accent-pink text-[#091424] px-2 py-0.5 rounded-full font-bold tracking-[0.08em]">FASTEST PATH</span>
          )}
        </div>
        <div className="text-white font-bold text-[16px] mt-1">{r.title}</div>
        {r.statute_reference && (
          <div className="text-text-muted text-[11px] mt-1 jea-mono">{r.statute_reference}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="jea-caption">Recovery Probability</div>
        <div className={cn("jea-mono font-bold text-[28px]", RECOVERY_TONE(r.recovery_probability_pct))}
             data-testid={`recommendation-prob-${r.id}`}>
          {r.recovery_probability_pct}%
        </div>
      </div>
    </div>

    <div className="mt-3 h-1.5 rounded-full bg-border-default overflow-hidden">
      <motion.div
        className={cn(
          "h-full rounded-full",
          r.recovery_probability_pct >= 70 ? "bg-success" :
          r.recovery_probability_pct >= 45 ? "bg-warning" : "bg-danger",
        )}
        initial={{ width: 0 }} animate={{ width: `${r.recovery_probability_pct}%` }}
        transition={{ duration: 0.6, delay: index * 0.06 + 0.15, ease: [0.4, 0, 0.2, 1] }}
      />
    </div>

    <div className="mt-4 grid grid-cols-3 gap-3 text-[12px]">
      <div>
        <div className="jea-caption">Timeline</div>
        <div className="jea-mono text-white font-semibold">{r.timeline_days} days</div>
      </div>
      <div>
        <div className="jea-caption">Cost Estimate</div>
        <div className="jea-mono text-white font-semibold">{centsToUSD(r.cost_estimate_cents)}</div>
      </div>
      <div>
        <div className="jea-caption">Feasibility</div>
        <div className="text-white font-semibold capitalize">{r.feasibility}</div>
      </div>
    </div>

    <div className="mt-4 pt-4 border-t border-border-default text-[13px] text-text-secondary leading-5">
      {r.notes}
    </div>
    {r.requires_court_order && (
      <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold tracking-[0.06em] text-warning uppercase">
        <Gavel size={11} /> Requires Court Order
      </div>
    )}
  </motion.div>
);

const HardOverrideCard = ({ override }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    className="bg-danger/10 border-2 border-danger rounded-[12px] p-6"
    data-testid="hard-override-card"
  >
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-full bg-danger/20 border border-danger flex items-center justify-center shrink-0">
        <ShieldAlert size={22} className="text-danger" />
      </div>
      <div className="flex-1">
        <div className="text-danger text-[12px] font-bold tracking-[0.12em] uppercase">STOP · Automatic Stay in Effect</div>
        <h4 className="text-white font-extrabold text-[22px] mt-1">HALT — All Enforcement Actions Prohibited</h4>
        <p className="text-text-secondary text-[14px] mt-3 leading-6">{override.message}</p>
        <div className="mt-4 grid grid-cols-2 gap-4 text-[13px] pt-4 border-t border-danger/40">
          <div>
            <div className="jea-caption">Chapter</div>
            <div className="text-white font-semibold jea-mono">Chapter {override.chapter}</div>
          </div>
          <div>
            <div className="jea-caption">Case Number</div>
            <div className="text-white font-semibold jea-mono">{override.case_number}</div>
          </div>
          <div>
            <div className="jea-caption">Court</div>
            <div className="text-white">{override.court}</div>
          </div>
          <div>
            <div className="jea-caption">Filed</div>
            <div className="text-white jea-date">{shortDate(override.filed_date)}</div>
          </div>
        </div>
        <div className="mt-4 text-[11px] text-danger font-bold tracking-[0.06em] uppercase">
          Statute: {override.statute}
        </div>
      </div>
    </div>
  </motion.div>
);

const ReportViewPage = () => {
  const { id: debtorId, report_id: reportId } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/reports/${reportId}`);
        if (!alive) return;
        setReport(data.report);
      } catch (e) {
        toast.error(formatApiError(e));
        nav(`/debtors/${debtorId}?tab=reports`, { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const recs = useMemo(() => report?.enforcement_recommendations || [], [report]);

  if (loading || !report) {
    return (
      <div className="min-h-[400px] flex items-center justify-center text-text-secondary" data-testid="report-loading">
        Loading report…
      </div>
    );
  }

  const snap = report.debtor_snapshot || {};
  const hasOverride = !!report.hard_override;
  const tierInfo = TIERS.find((t) => t.key === snap.tier) || TIERS[0];
  const status = snap.status || "active";

  const tierBadgeStyle =
    snap.tier === "gold" ? { background: "var(--color-tier-gold-bg-15)", border: "1px solid var(--color-tier-gold-border-40)", color: "var(--color-tier-gold)" } :
    snap.tier === "silver" ? { background: "rgba(192, 192, 192, 0.15)", border: "1px solid rgba(192, 192, 192, 0.40)", color: "var(--color-tier-silver)" } :
    { background: "rgba(205, 127, 50, 0.15)", border: "1px solid rgba(205, 127, 50, 0.40)", color: "var(--color-tier-bronze)" };

  return (
    <div className="space-y-6" data-testid="report-view-page">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <nav className="flex items-center gap-1 text-[13px] text-text-secondary">
            <button onClick={() => nav("/reports")} className="hover:text-accent-pink transition-colors" data-testid="rv-crumb-reports">
              Reports
            </button>
            <ChevronRight size={12} className="text-border-default" />
            <span className="text-white">{snap.full_name}</span>
          </nav>
        </div>
        <button
          type="button"
          onClick={() => nav(`/debtors/${debtorId}?tab=reports`)}
          className="text-text-secondary hover:text-white text-[13px] font-semibold inline-flex items-center gap-1"
          data-testid="rv-back"
        >
          <ArrowLeft size={12} /> Back to Debtor
        </button>
      </div>

      {/* Header card */}
      <div className="bg-bg-secondary border border-border-default rounded-[12px] p-6 flex items-start justify-between gap-6" data-testid="report-header-card">
        <div className="flex-1 min-w-0 space-y-3">
          <h1 className="text-white font-extrabold text-[22px] tracking-[-0.02em]" data-testid="report-debtor-name">
            {snap.full_name}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-[0.08em]"
              style={tierBadgeStyle}
              data-testid="report-tier-badge"
            >
              <Shield size={12} strokeWidth={2.5} />
              {tierInfo.label}
            </span>
            <span
              className={cn(
                "inline-flex px-2 py-1 rounded-full text-[11px] font-bold tracking-[0.08em]",
                status === "active" && "bg-success/15 text-success border border-success/40",
                status === "attention_required" && "bg-warning/15 text-warning border border-warning/40",
                status === "archived" && "bg-danger/15 text-danger border border-danger/40"
              )}
              data-testid="report-status-badge"
            >
              {(status || "active").replace("_", " ").toUpperCase()}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-6 pt-2">
            <div>
              <div className="jea-caption">Report Date</div>
              <div className="jea-date text-[14px] mt-1" data-testid="report-generated-at">
                {shortDate(report.generated_at)}
              </div>
            </div>
            <div>
              <div className="jea-caption">Brokers Run</div>
              <div className="jea-mono text-white font-semibold text-[14px] mt-1" data-testid="report-broker-count">
                {report.broker_count ?? 0}
              </div>
            </div>
            <div>
              <div className="jea-caption">Cost</div>
              <div className="jea-mono text-accent-pink font-bold text-[18px] mt-1" data-testid="report-total-cost">
                {centsToUSD(report.total_cost_cents || 0)}
              </div>
            </div>
          </div>
          <div className="pt-2 text-text-secondary text-[13px]">
            Case <span className="jea-mono">{snap.case_number || "—"}</span>
            {" · "}
            Judgment <span className="jea-mono text-accent-pink font-bold">{centsToUSD(snap.amount_cents || 0)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {report.query_run_id && (
            <JeaButton
              variant="outline-pink"
              className="gap-2"
              onClick={() => nav(`/debtors/${debtorId}/queries/${report.query_run_id}`)}
              data-testid="report-view-query-results"
            >
              <SearchIcon size={14} /> View Query Results
            </JeaButton>
          )}
          <JeaButton
            variant="outline-neutral"
            className="gap-2"
            onClick={async () => {
              try { await downloadAuthFile(`/reports/${reportId}/pdf`); }
              catch (e) { toast.error(formatApiError(e, "Couldn't download PDF.")); }
            }}
            data-testid="report-download-pdf"
          >
            <Download size={14} /> Download PDF
          </JeaButton>
          <JeaButton
            variant="outline-neutral"
            className="gap-2"
            onClick={async () => {
              try { await downloadAuthFile(`/reports/${reportId}/attorney-memo`); }
              catch (e) { toast.error(formatApiError(e, "Couldn't download memo.")); }
            }}
            data-testid="report-download-memo"
          >
            <FileSignature size={14} /> Download Attorney Memo
          </JeaButton>
        </div>
      </div>

      {/* 1. Summary */}
      <ConsolidatedAssetSummary summary={report.summary} consolidated={report.consolidated_assets} />

      {/* 2. Assets deep dive */}
      <Section title="Assets" subtitle="Cross-broker verified holdings and estimated values."
               testid="report-assets-section">
        {report.consolidated_assets?.real_property?.length ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={14} className="text-accent-pink" />
              <div className="jea-caption">Real Property</div>
            </div>
            <ul className="space-y-2 text-[13px]">
              {report.consolidated_assets.real_property.map((p, i) => (
                <li key={i} className="flex items-start justify-between gap-3 border-b border-border-default pb-2 last:border-0 last:pb-0">
                  <div>
                    <div className="text-white">{p.address}</div>
                    <div className="text-text-secondary text-[12px]">
                      {p.county} · APN <span className="jea-mono">{p.apn}</span>
                      {p.encumbered && p.lien_holder && <span> · Lien: {p.lien_holder}</span>}
                    </div>
                  </div>
                  <span className="jea-mono text-accent-pink font-bold">{centsToUSD(p.estimated_value_cents)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {report.consolidated_assets?.vehicles?.length ? (
          <div className="pt-4 border-t border-border-default">
            <div className="flex items-center gap-2 mb-2">
              <Car size={14} className="text-accent-pink" />
              <div className="jea-caption">Vehicles</div>
            </div>
            <ul className="space-y-2 text-[13px]">
              {report.consolidated_assets.vehicles.map((v, i) => (
                <li key={i} className="flex items-start justify-between gap-3 border-b border-border-default pb-2 last:border-0 last:pb-0">
                  <div>
                    <div className="text-white">{v.year} {v.make} {v.model}</div>
                    <div className="text-text-secondary text-[12px]">
                      VIN <span className="jea-mono">{v.vin}</span> · Reg {v.registration_state}
                      {v.lien_holder && <span> · Lien: {v.lien_holder}</span>}
                    </div>
                  </div>
                  <span className="jea-mono text-accent-pink font-bold">{centsToUSD(v.estimated_value_cents)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {report.consolidated_assets?.business_associations?.length ? (
          <div className="pt-4 border-t border-border-default">
            <div className="flex items-center gap-2 mb-2">
              <Landmark size={14} className="text-accent-pink" />
              <div className="jea-caption">Business Associations</div>
            </div>
            <ul className="space-y-1.5 text-[13px]">
              {report.consolidated_assets.business_associations.map((b, i) => (
                <li key={i} className="text-white">
                  <span className="font-semibold">{b.entity_name}</span>
                  <span className="text-text-secondary"> · {b.role} · <span className="capitalize">{b.status}</span></span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!report.consolidated_assets?.real_property?.length
          && !report.consolidated_assets?.vehicles?.length
          && !report.consolidated_assets?.business_associations?.length && (
            <div className="text-text-muted text-[13px]">No hard assets located across brokers.</div>
        )}
      </Section>

      {/* 3. Change Log */}
      <Section title="Change Log" subtitle={report.change_log?.length ? `${report.change_log.length} change(s) since last query.` : "No differences from the previous query."}
               testid="report-changelog-section">
        {report.change_log?.length ? (
          <div className="space-y-0">
            {report.change_log.map((c, i) => <ChangeRow key={i} c={c} />)}
          </div>
        ) : (
          <div className="text-text-muted text-[13px]">This appears to be the debtor's first query.</div>
        )}
      </Section>

      {/* 4. Alerts */}
      <Section title="Alerts" subtitle="Broker-level flags aggregated across all sources."
               testid="report-alerts-section">
        {report.alerts?.length ? (
          <div className="space-y-2">
            {report.alerts.map((a, i) => <AlertRow key={i} a={a} />)}
          </div>
        ) : (
          <div className="text-text-muted text-[13px]">No alerts generated by this query.</div>
        )}
      </Section>

      {/* 5. Strategy */}
      <Section
        title="Enforcement Strategy"
        subtitle={hasOverride
          ? "Bankruptcy detected — enforcement halted by automatic stay."
          : `Weighted top-2 recovery probability: ${report.recovery_probability_pct}%.`}
        testid="report-strategy-section"
      >
        {hasOverride ? (
          <HardOverrideCard override={report.hard_override} />
        ) : recs.length ? (
          <div className="space-y-3">
            {recs.map((r, i) => <RecommendationCard key={r.id} r={r} index={i} />)}
          </div>
        ) : (
          <div className="text-text-muted text-[13px]">No enforcement recommendations produced.</div>
        )}
      </Section>
    </div>
  );
};

export default ReportViewPage;
